import {ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit} from "@angular/core";
import {ActivatedRoute, Router} from "@angular/router";
import {MatDialog} from "@angular/material/dialog";
import {Store} from "@ngrx/store";
import {Subject, takeUntil} from "rxjs";
import {FundAiSetupActions} from "../store/fund-ai-setup.actions";
import {selectActiveDetail, selectIsResultPending} from "../store/fund-ai-setup.selectors";
import {
    AiSetupAcceptMethod,
    AiSetupResult,
    AiSetupResultStatus,
    AiSetupSection
} from "../../fund/generated-sources/api";
import {ConfirmDialogComponent, ConfirmDialogData} from "../confirm-dialog/confirm-dialog.component";

const DISCLAIMER_VERSION = "v1";

@Component({
    selector: "valumize-fund-ai-setup-viewer-page",
    templateUrl: "./fund-ai-setup-viewer-page.component.html",
    styleUrls: ["./fund-ai-setup-viewer-page.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FundAiSetupViewerPageComponent implements OnInit, OnDestroy {

    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly store = inject(Store);
    private readonly dialog = inject(MatDialog);

    readonly detail$ = this.store.select(selectActiveDetail);
    readonly SECTION = AiSetupSection;
    readonly STATUS = AiSetupResultStatus;

    taskInfoId!: number;
    expandedAssetIds = new Set<number>();
    private hasAutoExpanded = false;
    private readonly destroy$ = new Subject<void>();

    ngOnInit(): void {
        this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
            const idStr = params.get("taskInfoId");
            if (!idStr) return;
            this.taskInfoId = Number(idStr);
            this.store.dispatch(FundAiSetupActions.loadDetail({taskInfoId: this.taskInfoId}));
        });
        // Auto-expand all assets ONCE, on the first detail load — preserves user's collapse choices afterwards
        this.detail$.pipe(takeUntil(this.destroy$)).subscribe(detail => {
            if (!detail || this.hasAutoExpanded) return;
            const ids = new Set<number>();
            for (const r of detail.results) ids.add(r.assetId);
            this.expandedAssetIds = ids;
            this.hasAutoExpanded = true;
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    isPending$(resultId: number) {
        return this.store.select(selectIsResultPending(resultId));
    }

    accept(result: AiSetupResult) {
        // No confirmation modal — disclaimer is shown inline next to the button
        // and at the page header. One-click accept matches the per-item flow expected for ~50 items.
        this.store.dispatch(FundAiSetupActions.acceptResult({
            resultId: result.resultId,
            request: {
                acceptMethod: AiSetupAcceptMethod.Individual,
                disclaimerVersion: DISCLAIMER_VERSION
            },
            taskInfoId: this.taskInfoId
        }));
    }

    acceptAllForAsset(group: { assetId: number; assetName: string; results: AiSetupResult[] }) {
        const pending = group.results.filter(r => r.status === AiSetupResultStatus.PendingReview);
        if (pending.length === 0) return;

        const ref = this.dialog.open<ConfirmDialogComponent, ConfirmDialogData, boolean>(ConfirmDialogComponent, {
            data: {
                title: `Accept all ${pending.length} item${pending.length === 1 ? "" : "s"} for ${group.assetName || "this asset"}?`,
                message: "By accepting all, you confirm you've reviewed each item and take responsibility for the content's accuracy.",
                confirmLabel: `Yes, accept ${pending.length}`,
                cancelLabel: "Cancel"
            }
        });
        ref.afterClosed().subscribe(confirmed => {
            if (!confirmed) return;
            for (const result of pending) {
                this.store.dispatch(FundAiSetupActions.acceptResult({
                    resultId: result.resultId,
                    request: {
                        acceptMethod: AiSetupAcceptMethod.Bulk,
                        disclaimerVersion: DISCLAIMER_VERSION
                    },
                    taskInfoId: this.taskInfoId
                }));
            }
        });
    }

    goBack() {
        history.back();
    }

    skip(result: AiSetupResult) {
        this.store.dispatch(FundAiSetupActions.skipResult({
            resultId: result.resultId,
            taskInfoId: this.taskInfoId
        }));
    }

    isExpanded(assetId: number): boolean {
        return this.expandedAssetIds.has(assetId);
    }

    onPanelOpened(assetId: number) {
        this.expandedAssetIds = new Set(this.expandedAssetIds).add(assetId);
    }

    onPanelClosed(assetId: number) {
        const next = new Set(this.expandedAssetIds);
        next.delete(assetId);
        this.expandedAssetIds = next;
    }

    pendingCount(group: { results: AiSetupResult[] }): number {
        return group.results.filter(r => r.status === AiSetupResultStatus.PendingReview).length;
    }

    groupedByAsset(results: AiSetupResult[]): { assetId: number; assetName: string; results: AiSetupResult[] }[] {
        const map = new Map<number, { assetId: number; assetName: string; results: AiSetupResult[] }>();
        for (const r of results) {
            if (!map.has(r.assetId)) {
                map.set(r.assetId, {assetId: r.assetId, assetName: r.assetName, results: []});
            }
            map.get(r.assetId)!.results.push(r);
        }
        return Array.from(map.values());
    }

    sectionLabel(section: AiSetupSection): string {
        switch (section) {
            case AiSetupSection.CompanyDescription: return "Company description";
            case AiSetupSection.EndMarkets: return "End markets & customers";
            case AiSetupSection.Swot: return "SWOT";
            case AiSetupSection.GeneralInfo: return "General info";
            case AiSetupSection.ComparableCompanies: return "Comparable companies";
            default: return section;
        }
    }
}
